import { useEffect, useState } from "react";
import { ArrowLeft, CalendarDays, Mail, MapPin, Phone, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getStudentProfile } from "../../lib/StudentProfileApi";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "null");
        const userId = storedUser?.userID || storedUser?.result?.userID || storedUser?.userId || storedUser?.id;
        if (!userId) throw new Error("Student ID is missing. Please log in again.");
        setProfile(await getStudentProfile(userId));
      } catch (loadError) {
        setError(loadError?.response?.data?.message || loadError.message || "Unable to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const fullName = [profile?.FirstName, profile?.LastName].filter(Boolean).join(" ") || "Student";
  const initials = fullName.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  const dateOfBirth = profile?.DateOfBirth?.startsWith("0001-") ? "Not provided" : profile?.DateOfBirth ? new Date(profile.DateOfBirth).toLocaleDateString("en-IN") : "Not provided";

  return (
    <div className="min-h-full bg-[#f7f3ea] px-4 py-6 font-sans text-[#211b4e] sm:px-8 sm:py-8">
      <div className="mx-auto w-full max-w-[1050px]">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[.12em] text-[#8b84a3]">Student account</p>
            <h1 className="font-sans text-3xl font-extrabold tracking-[-.04em] text-[#3f35c7] sm:text-4xl">My profile</h1>
          </div>
          <button className="grid h-10 w-10 place-items-center rounded-full border border-[#ddd4c4] bg-white text-[#3f35c7] transition hover:bg-[#3f35c7] hover:text-white" type="button" onClick={() => navigate(-1)} aria-label="Go back"><ArrowLeft size={18} /></button>
        </header>

        {loading && <div className="rounded-2xl bg-white p-10 text-center text-[#716a88]">Loading profile…</div>}
        {error && <div className="rounded-2xl bg-white p-10 text-center text-[#a8442b]">{error}</div>}

        {!loading && !error && profile && (
          <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
            <section className="rounded-2xl bg-[#3f35c7] p-7 text-center text-white shadow-[0_10px_24px_rgba(63,53,199,.12)]">
              {profile.Photo || profile.ProfileUrl ? <img className="mx-auto h-28 w-28 rounded-full object-cover" src={profile.Photo || profile.ProfileUrl} alt={fullName} /> : <div className="mx-auto grid h-28 w-28 place-items-center rounded-full bg-[#655bd4] text-4xl font-extrabold">{initials}</div>}
              <h2 className="mt-5 text-2xl font-extrabold">{fullName}</h2>
              <p className="mt-1 text-sm capitalize text-[#e9e7ff]">{profile.RoleName || "Student"}</p>
              <p className="mt-5 rounded-full bg-[#655bd4] px-4 py-2 text-xs font-bold">{profile.SchoolName || "School not available"}</p>
            </section>

            <section className="rounded-2xl border border-[#e7dfd0] bg-white p-5 sm:p-7">
              <h2 className="mb-5 text-xl font-extrabold text-[#3f35c7]">Personal details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Detail icon={Mail} label="Email" value={profile.EmailId} />
                <Detail icon={Phone} label="Mobile number" value={profile.MobileNumber} />
                <Detail icon={UserRound} label="Gender" value={profile.Gender || "Not provided"} />
                <Detail icon={CalendarDays} label="Date of birth" value={dateOfBirth} />
                <Detail icon={MapPin} label="City" value={profile.CityName || "Not provided"} />
                <Detail icon={UserRound} label="Admission number" value={profile.AdmissionNumber || "Not provided"} />
              </div>
              <div className="mt-7 grid gap-4 border-t border-[#eee7dc] pt-6 sm:grid-cols-2">
                <InfoList title="Classes" items={profile.UserClasses?.map((item) => item.classname) || []} />
                <InfoList title="Subjects" items={profile.UserSubjects?.map((item) => item.SubjectName) || []} />
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const Detail = ({ icon: Icon, label, value }) => <div className="flex min-w-0 items-start gap-3"><span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#eeeaff] text-[#3f35c7]"><Icon size={17} /></span><div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-wide text-[#8b84a3]">{label}</p><p className="mt-1 truncate text-sm font-semibold text-[#211b4e]">{value || "Not provided"}</p></div></div>;

const InfoList = ({ title, items }) => <div><p className="text-[11px] font-bold uppercase tracking-wide text-[#8b84a3]">{title}</p><div className="mt-2 flex flex-wrap gap-2">{items.length ? items.map((item) => <span className="rounded-full bg-[#eeeaff] px-3 py-1.5 text-xs font-bold text-[#3f35c7]" key={item}>{item}</span>) : <span className="text-sm text-[#716a88]">Not provided</span>}</div></div>;

export default ProfilePage;
